/**
 * 1.8.0 rights: the pure part of the confirm and override block (Preflight.tsx RightsCheck; docs/dev/rights-1.8.0-build.md).
 * Preflight and a start's 409 give the rights problems with a severity: `block` stops the start as every other
 * problem does, `override` needs a rights manager's reason, `confirm` needs "I've read the rights restrictions".
 * The start then sends `rights: { confirm, override }` (contracts RightsAck).
 */
import {
  RIGHTS_CONFIRMATION_REQUIRED, RIGHTS_OVERRIDE_REQUIRED, RIGHTS_PROBLEM_CODES, isRightsProblemCode, rightsOutcome, rightsProblemMessage,
  type RightsAck, type RightsSettings, type RightsSummary,
} from '@prism/contracts';
import type { PreflightProblem } from './Preflight';

/** A problem that stops the start whatever the user answers (every problem before 1.8.0, and the policy's Block). */
export const isBlockingProblem = (p: PreflightProblem): boolean => !p.severity || p.severity === 'block';

/** A problem the user can answer: a rights manager's override, or the confirmation. */
export const isAckProblem = (p: PreflightProblem): boolean => p.severity === 'override' || p.severity === 'confirm';

export interface RightsAckState { confirmed: boolean; reason: string }
export const EMPTY_RIGHTS_ACK: RightsAckState = { confirmed: false, reason: '' };

/** What the problems ask for. */
export function rightsAckNeeds(problems: readonly PreflightProblem[]): { confirm: boolean; override: boolean } {
  return { confirm: problems.some((p) => p.severity === 'confirm'), override: problems.some((p) => p.severity === 'override') };
}

/**
 * Whether the answers let the start go: an override needs `rights.manage` and a reason; a confirmation needs the box.
 * The checkbox also covers the problems a rights manager overrides (they have read them too).
 */
export function rightsAckReady(problems: readonly PreflightProblem[], ack: RightsAckState, canManage: boolean): boolean {
  const needs = rightsAckNeeds(problems);
  if (needs.override && !(canManage && ack.reason.trim())) return false;
  if ((needs.confirm || needs.override) && !ack.confirmed) return false;
  return true;
}

/** The request's `rights` field; undefined when nothing is asked (rights off, or no rights problems): no key is sent. */
export function rightsAckBody(problems: readonly PreflightProblem[], ack: RightsAckState, canManage: boolean): RightsAck | undefined {
  const needs = rightsAckNeeds(problems);
  if (!needs.confirm && !needs.override) return undefined;
  const out: RightsAck = {};
  if (ack.confirmed) out.confirm = true;
  const reason = ack.reason.trim().slice(0, 500);
  if (needs.override && canManage && reason) out.override = { reason };
  return Object.keys(out).length ? out : undefined;
}

/** `{ rights }` to spread into a start's JSON body, or nothing. */
export const rightsAckField = (problems: readonly PreflightProblem[], ack: RightsAckState, canManage: boolean): { rights?: RightsAck } => {
  const rights = rightsAckBody(problems, ack, canManage);
  return rights ? { rights } : {};
};

/** The rights problems of a start's 409 (RIGHTS_OVERRIDE_REQUIRED, RIGHTS_CONFIRMATION_REQUIRED), or null for any other error. */
export function rightsProblemsOfError(err: unknown): PreflightProblem[] | null {
  const e = err as { status?: number; code?: string; details?: { problems?: unknown } } | null;
  if (!e || e.status !== 409 || (e.code !== RIGHTS_OVERRIDE_REQUIRED && e.code !== RIGHTS_CONFIRMATION_REQUIRED)) return null;
  const list = Array.isArray(e.details?.problems) ? (e.details.problems as PreflightProblem[]) : [];
  return list.filter((p) => p && typeof p.code === 'string');
}

/**
 * Whether the server lets this caller override (RIGHTS_OVERRIDE_REQUIRED's details.canOverride: rights.manage, and
 * never through an assistant token), or null when the error doesn't say.
 */
export function canOverrideOfError(err: unknown): boolean | null {
  const e = err as { status?: number; code?: string; details?: { canOverride?: unknown } } | null;
  if (!e || e.status !== 409 || e.code !== RIGHTS_OVERRIDE_REQUIRED) return null;
  return typeof e.details?.canOverride === 'boolean' ? e.details.canOverride : null;
}

/** Two problem lists together, each (code, asset) once; the later list's copy wins. */
export function mergeProblems(a: readonly PreflightProblem[], b: readonly PreflightProblem[]): PreflightProblem[] {
  const key = (p: PreflightProblem) => `${p.code}|${p.assetId ?? ''}|${p.nodeId ?? ''}`;
  const out = new Map<string, PreflightProblem>();
  for (const p of [...a, ...b]) out.set(key(p), p);
  return [...out.values()];
}

/** The rights problems the user answers (override and confirm), from a whole preflight list. */
export const ackProblemsOf = (problems: readonly PreflightProblem[] | undefined): PreflightProblem[] =>
  (problems ?? []).filter((p) => isAckProblem(p) && isRightsProblemCode(p.code));

/** The problems per asset, in the order first met (the block lists each asset once with its reasons). */
export function problemsByAsset(problems: readonly PreflightProblem[]): Array<{ key: string; title: string | null; problems: PreflightProblem[] }> {
  const groups = new Map<string, { key: string; title: string | null; problems: PreflightProblem[] }>();
  for (const p of problems) {
    const key = p.assetId ?? `${p.code}:${p.message}`;
    const g = groups.get(key) ?? { key, title: p.title ?? null, problems: [] };
    g.problems.push(p);
    groups.set(key, g);
  }
  return [...groups.values()];
}

/**
 * The rights problem of one asset worked out in the browser, for a start with no preflight of its own (Share): its
 * rights summary under the workspace's policy. None while rights are off (no `rights` on the asset) or when the
 * policy asks nothing. The server checks again when the link is made.
 */
export function assetRightsProblems(
  asset: { id: string; title: string; rights?: RightsSummary | null },
  settings: Pick<RightsSettings, 'enabled' | 'policy'> | null | undefined,
): PreflightProblem[] {
  const r = asset.rights;
  if (!r || !settings?.enabled || r.state === 'cleared') return [];
  const severity = rightsOutcome(r.state, settings.policy);
  if (!severity) return [];
  return [{ code: RIGHTS_PROBLEM_CODES[r.state], message: rightsProblemMessage(asset.title, r), severity, assetId: asset.id, title: asset.title }];
}
